import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  Select,
  Typography,
  Paper,
  FormHelperText,
  InputLabel,
  FormControl,
  CircularProgress,
  TextField,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import apiUsers from "../../services/authServiceLogin";

const CurrencySettings = () => {
  const [initialCurrency, setInitialCurrency] = useState(null);
  const [currencyValue, setCurrencyValue] = useState();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCurrency = async () => {
      try {
        const res = await apiUsers.getSetting();
        const currency = res?.data?.data?.currency || "USD";
        setInitialCurrency(currency);
        setCurrencyValue(res?.data?.data?.conversionRate)
      } catch (err) {
        toast.error("Failed to load currency settings");
      } finally {
        setLoading(false);
      }
    };

    fetchCurrency();
  }, []);

  const validationSchema = Yup.object({
    currency: Yup.string().required("Currency is required"),
    conversionRate: Yup.string()
      .when("currency", {
        is: "KHR",
        then: (schema) =>
          schema
            .required("Khmer currency value is required")
            .matches(/^\d+(\.\d{1,2})?$/, "Enter a valid number"),
        otherwise: (schema) => schema.notRequired(),
      }),
  });

  const handleSave = async (values, { setSubmitting }) => {
    try {
      const payload = { ...values };
  
      if (payload.currency === "USD") {
        delete payload.conversionRate;
      } else if (payload.conversionRate) {
        payload.conversionRate = parseFloat(payload.conversionRate);
      }
  
      const res = await apiUsers.updateCurrency(payload);
      toast.success(res?.data?.message);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong!");
    } finally {
      setSubmitting(false);
    }
  };
  

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card sx={{ minHeight: "80vh", display: "flex", flexDirection: "column" }}>
      <Helmet>
        <title>Currency Management</title>
      </Helmet>
      <CardHeader
        title="Currency Management"
        titleTypographyProps={{ variant: "h4" }}
      />
      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Paper elevation={3} sx={{ p: 3, maxWidth: 500, width: "100%", mt: "-200px" }}>
          <Formik
            initialValues={{ currency: initialCurrency, conversionRate: currencyValue }}
            validationSchema={validationSchema}
            onSubmit={handleSave}
            enableReinitialize
          >
            {({ values, errors, touched, handleChange, isSubmitting, dirty }) => (
              <Form>
                <Typography variant="h6" textAlign="start">
                  Select Currency
                </Typography>
                <FormControl fullWidth sx={{ mt: 2 }} error={Boolean(touched.currency && errors.currency)}>
                  <InputLabel id="currency-label">Currency</InputLabel>
                  <Select
                    labelId="currency-label"
                    id="currency"
                    name="currency"
                    value={values.currency}
                    onChange={handleChange}
                    label="Currency"
                  >
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="KHR">KHR</MenuItem>
                  </Select>
                  {touched.currency && errors.currency && (
                    <FormHelperText>{errors.currency}</FormHelperText>
                  )}
                </FormControl>

                {values.currency === "KHR" && (
                  <TextField
                    fullWidth
                    id="conversionRate"
                    name="conversionRate"
                    label="Enter Khmer Value"
                    value={values.conversionRate}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^\d*\.?\d*$/.test(val) && val.replace('.', '').length <= 8) {
                        handleChange(e);
                      }
                    }}
                    error={touched.conversionRate && Boolean(errors.conversionRate)}
                    helperText={touched.conversionRate && errors.conversionRate}
                    sx={{ mt: 3 }}
                  />
                )}

                <Box mt={4} display="flex" justifyContent="center">
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    type="submit"
                    disabled={isSubmitting || !dirty}
                  >
                    Save
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Paper>
      </CardContent>
    </Card>
  );
};

export default CurrencySettings;
